/**
 * Companies House tab — UK company-registry snapshot for the Customer File.
 *
 * Auto-resolves the CRN by name on first open (cached on the case doc).
 * Shows:
 *   • Overview card  — status, type, incorporation, registered office, SIC
 *   • Officers       — active + resigned, with appointment + resignation dates
 *   • Filing history — latest 50 with type / description / date
 *   • Charges        — secured debentures with persons entitled
 *
 * Ops can override the auto-pick via the "Link to a different company"
 * search dialog (e.g. if the customer name was ambiguous).
 */
import { useEffect, useState } from "react";
import { ukDate } from "@/lib/utils";
import { Loader2, Building2, MapPin, Users, FileText, Lock, Search, Link2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  getCaseCompaniesHouse, searchCompaniesHouseForCase, linkCompaniesHouse, unlinkCompaniesHouse,
} from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const STATUS_TONE = {
  active:       "bg-emerald-50 text-emerald-700 border-emerald-300",
  dissolved:    "bg-rose-50 text-rose-700 border-rose-300",
  liquidation:  "bg-rose-50 text-rose-700 border-rose-300",
  "in-administration": "bg-amber-50 text-amber-800 border-amber-300",
  "voluntary-arrangement": "bg-amber-50 text-amber-800 border-amber-300",
};

export default function CompaniesHouseTab({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setErr(null);
      const r = await getCaseCompaniesHouse(caseRow.case_id);
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load Companies House data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseRow.case_id]);

  if (loading && !data) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid="ch-tab-loading">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading Companies House…
      </div>
    );
  }

  if (err) {
    return (
      <div className="m-4 p-3 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-900" data-testid="ch-tab-error">
        <div className="font-semibold mb-1">Could not load Companies House</div>
        <div className="text-xs">{err}</div>
      </div>
    );
  }

  if (!data?.ok) {
    return (
      <div className="m-4 p-6 rounded-md border bg-slate-50 text-sm" data-testid="ch-tab-not-linked">
        <div className="font-semibold mb-2 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Not linked to Companies House
        </div>
        <p className="text-muted-foreground text-xs mb-3">
          {data?.message || "No automatic match found. Search and link manually."}
        </p>
        <LinkCompanyDialog caseRow={caseRow} onLinked={load} />
      </div>
    );
  }

  const profile = data.profile || {};
  const officers = data.officers?.officers || [];
  const filings = data.filings?.filings || [];
  const charges = data.charges?.charges || [];
  const status = (profile.company_status || "").toLowerCase();
  const statusTone = STATUS_TONE[status] || "bg-slate-50 text-slate-700 border-slate-300";

  return (
    <div className="space-y-4" data-testid="ch-tab">
      {/* Header bar */}
      <div className="bg-white rounded-lg border shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap">
        <Building2 className="w-5 h-5 text-slate-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm">{profile.company_name || caseRow.customer_name}</span>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${statusTone}`} data-testid="ch-status-badge">
              {status || "unknown"}
            </Badge>
            <a
              href={`https://find-and-update.company-information.service.gov.uk/company/${data.company_number}`}
              target="_blank" rel="noreferrer"
              className="text-[11px] text-indigo-600 hover:underline"
              data-testid="ch-open-on-gov-uk"
            >
              {data.company_number} ↗
            </a>
            {data.auto_picked && (
              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">auto-matched</Badge>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {profile.type && <span>{profile.type.toUpperCase()}</span>}
            {profile.date_of_creation && <span> · Incorporated {profile.date_of_creation}</span>}
            {profile.jurisdiction && <span> · {profile.jurisdiction}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <LinkCompanyDialog caseRow={caseRow} onLinked={load} buttonLabel="Re-link" />
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={load} title="Refresh" data-testid="ch-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Overview cards row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InfoCard icon={<MapPin className="w-3.5 h-3.5" />} label="Registered office">
          <div className="text-xs text-slate-700">{profile.registered_office_address || "—"}</div>
        </InfoCard>
        <InfoCard icon={<FileText className="w-3.5 h-3.5" />} label="Accounts">
          <div className="text-xs text-slate-700">
            {profile.accounts?.next_due
              ? <>Next due <strong>{profile.accounts.next_due}</strong></>
              : profile.accounts?.last_accounts?.made_up_to
              ? <>Last filed {profile.accounts.last_accounts.made_up_to}</>
              : "—"}
            {profile.accounts?.overdue && <Badge variant="outline" className="ml-1 text-[10px] bg-rose-50 text-rose-700 border-rose-300">overdue</Badge>}
          </div>
        </InfoCard>
        <InfoCard icon={<FileText className="w-3.5 h-3.5" />} label="Confirmation statement">
          <div className="text-xs text-slate-700">
            {profile.confirmation_statement?.next_due
              ? <>Next due <strong>{profile.confirmation_statement.next_due}</strong></>
              : "—"}
            {profile.confirmation_statement?.overdue && <Badge variant="outline" className="ml-1 text-[10px] bg-rose-50 text-rose-700 border-rose-300">overdue</Badge>}
          </div>
        </InfoCard>
      </div>

      {/* SIC codes row */}
      {profile.sic_codes?.length > 0 && (
        <div className="bg-white rounded-lg border px-4 py-2 text-xs">
          <span className="text-muted-foreground mr-2">SIC:</span>
          {profile.sic_codes.map((s) => (
            <Badge key={s} variant="outline" className="mr-1 text-[10px]">{s}</Badge>
          ))}
        </div>
      )}

      {/* Officers + Charges side by side on wide screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Officers */}
        <div className="bg-white rounded-lg border shadow-sm" data-testid="ch-officers">
          <div className="px-4 py-2.5 border-b flex items-center gap-2 text-xs">
            <Users className="w-3.5 h-3.5 text-slate-600" />
            <span className="font-semibold">Officers</span>
            <span className="text-muted-foreground">
              {data.officers?.active_count || 0} active · {data.officers?.resigned_count || 0} resigned
            </span>
          </div>
          <div className="divide-y max-h-72 overflow-y-auto">
            {officers.length === 0 && (
              <div className="px-4 py-3 text-xs text-muted-foreground">No officer records returned.</div>
            )}
            {officers.map((o, idx) => (
              <div key={`${o.name}-${idx}`} className="px-4 py-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{o.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500">{o.officer_role}</span>
                  {o.is_active ? (
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300">resigned {o.resigned_on}</Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Appointed {o.appointed_on || "—"}
                  {o.occupation && <> · {o.occupation}</>}
                  {o.nationality && <> · {o.nationality}</>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charges */}
        <div className="bg-white rounded-lg border shadow-sm" data-testid="ch-charges">
          <div className="px-4 py-2.5 border-b flex items-center gap-2 text-xs">
            <Lock className="w-3.5 h-3.5 text-slate-600" />
            <span className="font-semibold">Charges (Debentures)</span>
            <span className="text-muted-foreground">
              {data.charges?.unsatisfied_count || 0} outstanding · {data.charges?.total_count || 0} total
            </span>
          </div>
          <div className="divide-y max-h-72 overflow-y-auto">
            {charges.length === 0 && (
              <div className="px-4 py-3 text-xs text-muted-foreground">No charges registered.</div>
            )}
            {charges.map((c) => (
              <div key={c.charge_number} className="px-4 py-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">#{c.charge_number}</span>
                  <Badge variant="outline" className={`text-[10px] ${c.status === "outstanding" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>
                    {c.status}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{c.classification || ""}</span>
                </div>
                {c.persons_entitled?.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    In favour of: {c.persons_entitled.join(", ")}
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Created {c.created_on}
                  {c.satisfied_on && <> · Satisfied {c.satisfied_on}</>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filing history */}
      <div className="bg-white rounded-lg border shadow-sm" data-testid="ch-filings">
        <div className="px-4 py-2.5 border-b flex items-center gap-2 text-xs">
          <FileText className="w-3.5 h-3.5 text-slate-600" />
          <span className="font-semibold">Filing history</span>
          <span className="text-muted-foreground">{data.filings?.total_count || filings.length} on record</span>
        </div>
        <div className="divide-y max-h-96 overflow-y-auto">
          {filings.length === 0 && (
            <div className="px-4 py-3 text-xs text-muted-foreground">No filings returned.</div>
          )}
          {filings.map((f) => (
            <div key={f.transaction_id} className="px-4 py-2 text-xs flex items-start gap-3">
              <span className="font-mono text-[10px] tabular-nums text-slate-500 w-20 shrink-0">{ukDate(f.date)}</span>
              <span className="font-mono text-[10px] uppercase bg-slate-100 text-slate-700 px-1.5 rounded shrink-0 w-14 text-center">{f.type}</span>
              <span className="text-slate-700 flex-1">{f.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, children }) {
  return (
    <div className="bg-white rounded-lg border shadow-sm px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1">
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function LinkCompanyDialog({ caseRow, onLinked, buttonLabel = "Link to Companies House" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(caseRow.customer_name || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const runSearch = async () => {
    try {
      setBusy(true);
      const r = await searchCompaniesHouseForCase(caseRow.case_id, query);
      setResults(r.items || []);
    } catch (e) {
      toast.error("Search failed: " + errorDetailMessage(e, e?.message || ""));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (open) runSearch(); }, [open]);

  const pick = async (item) => {
    try {
      await linkCompaniesHouse(caseRow.case_id, {
        company_number: item.company_number,
        company_name:   item.company_name,
      });
      toast.success(`Linked to ${item.company_name} (${item.company_number})`);
      setOpen(false);
      onLinked?.();
    } catch (e) {
      toast.error("Link failed: " + errorDetailMessage(e, e?.message || ""));
    }
  };

  const unlink = async () => {
    try {
      await unlinkCompaniesHouse(caseRow.case_id);
      toast.success("Unlinked");
      setOpen(false);
      onLinked?.();
    } catch (e) {
      toast.error("Unlink failed: " + errorDetailMessage(e, e?.message || ""));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="ch-link-button">
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Link to a Companies House record</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search by company name…"
            data-testid="ch-link-search-input"
          />
          <Button size="sm" onClick={runSearch} disabled={busy} data-testid="ch-link-search-button">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="divide-y max-h-80 overflow-y-auto rounded-md border">
          {results.length === 0 && !busy && (
            <div className="px-4 py-3 text-xs text-muted-foreground">No results.</div>
          )}
          {results.map((r) => (
            <button
              key={r.company_number}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs"
              data-testid={`ch-link-result-${r.company_number}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{r.company_name}</span>
                <span className="font-mono text-[10px] text-slate-500">{r.company_number}</span>
                <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.company_status] || ""}`}>
                  {r.company_status}
                </Badge>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {r.address_snippet}
                {r.date_of_creation && <> · Incorporated {r.date_of_creation}</>}
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={unlink} data-testid="ch-unlink-button">
            Unlink
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
